import { defineStore } from 'pinia'

const FONT_SIZE_KEYS = [
  'xxxxxl-font-size',
  'xxxxl-font-size',
  'xxxl-font-size',
  'xxl-font-size',
  'xl-font-size',
  'l-font-size',
  'm-font-size',
  'regular-font-size',
  's-font-size',
  'xs-font-size',
  'xxs-font-size'
]

export const HIGH_CONTRAST_TOGGLE = 'show_high_contrast'
export const GRAY_SCALE_TOGGLE = 'show_gray_scale'
export const HIGHLIGHT_LINKS_TOGGLE = 'show_highlight_links'
export const INVERT_COLORS_TOGGLE = 'show_invert_colors'
export const HIDE_ACCESSIBILITY = 'hide_accessibility'

export const useAccessibilityStore = defineStore('accessibility', {
  state: () => {
    const state = {
      originalFontSizes: {},
      fontSizePercentage: 100,
      highContrastEnabled:
        localStorage.getItem(HIGH_CONTRAST_TOGGLE) === 'true',
      grayScaleEnabled: localStorage.getItem(GRAY_SCALE_TOGGLE) === 'true',
      highlightLinksEnabled:
        localStorage.getItem(HIGHLIGHT_LINKS_TOGGLE) === 'true',
      invertColorsEnabled:
        localStorage.getItem(INVERT_COLORS_TOGGLE) === 'true',
      showAccessibility: !localStorage.getItem(HIDE_ACCESSIBILITY),
      styleTag: null
    }

    FONT_SIZE_KEYS.forEach((key) => {
      state.originalFontSizes[key] = Number.parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(`--${key}`)
      )
    })

    const savedPercentage = localStorage.getItem('fontSizePercentage')
    if (savedPercentage !== null) {
      state.fontSizePercentage = Number.parseInt(savedPercentage)
    }

    return state
  },
  getters: {
    fontSizeDifference: (state) => {
      return state.fontSizePercentage
    },
    isAccessibilityEnabled() {
      return (
        this.fontSizePercentage !== 100 ||
        this.highContrastEnabled ||
        this.grayScaleEnabled ||
        this.highlightLinksEnabled ||
        this.invertColorsEnabled
      )
    }
  },
  actions: {
    hidePermanently() {
      localStorage.setItem(HIDE_ACCESSIBILITY, 'true')
      this.showAccessibility = false
    },
    setFontSize(percentage) {
      this.fontSizePercentage = percentage
      this.updateCSSVariables()
    },
    resetFontSize() {
      this.fontSizePercentage = 100
      this.updateCSSVariables()
    },
    updateCSSVariables() {
      if (!this.styleTag) {
        const headTag = document.getElementsByTagName('head')[0]
        this.styleTag = document.createElement('style')
        headTag.appendChild(this.styleTag)
      }

      let cssVariables = ':root {'
      FONT_SIZE_KEYS.forEach((key) => {
        const newSize =
          (this.originalFontSizes[key] * this.fontSizePercentage) / 100
        cssVariables += `--${key}: ${newSize}px; `
      })
      cssVariables += '}'

      this.styleTag.innerHTML = cssVariables

      localStorage.setItem('fontSizePercentage', this.fontSizePercentage)

      this.setGrayscale(this.grayScaleEnabled)
      this.setHighlightLinks(this.highlightLinksEnabled)
      this.setInvertColors(this.invertColorsEnabled)
    },
    setHighContrast(enabled) {
      this.highContrastEnabled = enabled
      enabled
        ? localStorage.setItem(HIGH_CONTRAST_TOGGLE, 'true')
        : localStorage.removeItem(HIGH_CONTRAST_TOGGLE)
    },
    setGrayscale(enabled) {
      this.grayScaleEnabled = enabled
      enabled
        ? localStorage.setItem(GRAY_SCALE_TOGGLE, 'true')
        : localStorage.removeItem(GRAY_SCALE_TOGGLE)
      this.updateFilters()
    },
    setHighlightLinks(enabled) {
      this.highlightLinksEnabled = enabled
      enabled
        ? localStorage.setItem(HIGHLIGHT_LINKS_TOGGLE, 'true')
        : localStorage.removeItem(HIGHLIGHT_LINKS_TOGGLE)
      enabled
        ? document.documentElement.classList.add('highlight-links')
        : document.documentElement.classList.remove('highlight-links')
    },
    setInvertColors(enabled) {
      this.invertColorsEnabled = enabled
      enabled
        ? localStorage.setItem(INVERT_COLORS_TOGGLE, 'true')
        : localStorage.removeItem(INVERT_COLORS_TOGGLE)
      this.updateFilters()
    },
    updateFilters() {
      const filters = []
      if (this.grayScaleEnabled) filters.push('grayscale(100%)')
      if (this.invertColorsEnabled) filters.push('invert(100%)')
      document.documentElement.style.filter = filters.join(' ')
    }
  }
})
